import { useState, useEffect, useMemo, useCallback } from 'react'
import { getAdminContracts, evaluateAdminContract, getPortalContracts, evaluatePortalContract } from '../../api'
import { useAuth } from '../../AuthProvider'
import { providedThroughText } from '../../utils/providedThrough'
import './ContractView.css'

// A stored contract (queue item 181), used by the customer portal's Contract tab and the same tab on the consultant's customer page, laid out as the contract-editor
// wireframe (wireframes/xlr8-contract-editor.html): terms header, subscription lines
// with each line's counting rule and generated SQL, partition check, cost bar with
// contract value, and a bill estimate. Every number comes from the evaluate endpoint
// (compiled rules, read-only, against synced data). Read-only for now: terms are
// entered on the backend.

const money = v => (v == null ? '—' : Number(v).toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }))
const money2 = v => (v == null ? '—' : Number(v).toLocaleString('en-US', { style: 'currency', currency: 'USD' }))
const moneyK = v => (Math.abs(v) >= 1e6 ? `$${(v / 1e6).toFixed(2)}M` : money(v))
const num = v => (v == null ? '—' : Number(v).toLocaleString('en-US'))
const ymKey = d => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
const ymLabel = ym => new Date(+ym.slice(0, 4), +ym.slice(5) - 1, 1).toLocaleString('en-US', { month: 'long', year: 'numeric' })
const ymShort = ym => new Date(+ym.slice(0, 4), +ym.slice(5) - 1, 1).toLocaleString('en-US', { month: 'short', year: 'numeric' }).toUpperCase()

const MODEL_LABEL = {
  pepm_by_type: 'PEPM by employee type', flat_pepm: 'Flat PEPM', per_seat: 'Per seat', per_unit: 'Per unit',
  per_transaction: 'Per transaction', tiered: 'Tiered', flat_fee: 'Flat fee',
}
const OP_LABEL = {
  eq: '=', neq: '≠', in: 'in', not_in: 'not in', gt: '>', gte: '≥', lt: '<', lte: '≤',
  is_null: 'is empty', is_not_null: 'has a value', present_in_period: 'present', absent_in_period: 'none',
}

// A readable description of one condition, as the wireframe's rule builder shows it.
function describe(c) {
  if (c.type === 'activity') {
    const f = c.filter ? ` where ${c.filter.field} ${OP_LABEL[c.filter.op] || c.filter.op} ${c.filter.value}` : ''
    return { src: c.source, field: `activity in period${f}`, op: 'is', val: c.op === 'present_in_period' ? 'present' : 'none' }
  }
  if (c.type === 'calendar') {
    return { src: c.reference, field: 'pay date for pay group in period', op: 'is', val: c.op === 'present_in_period' ? 'present' : 'none (no-pay group)' }
  }
  const src = c.type === 'lookup' ? c.reference : c.source
  const val = Array.isArray(c.value) ? c.value.join(', ') : (c.value == null ? '' : String(c.value))
  return { src, field: c.field, op: OP_LABEL[c.op] || c.op, val }
}
const isUnverified = (c, joins) => {
  const src = c.type === 'lookup' || c.type === 'calendar' ? c.reference : c.source
  return (joins || []).some(j => j.includes(src))
}

// The data's as-of date: the OLDEST sync among the sources the rules read, since
// the stalest source bounds what the count can know.
const asOfOf = r => Object.values(r?.data_as_of || {}).filter(Boolean).sort()[0] || null
const prevMonth = m => ymKey(new Date(+m.slice(0, 4), +m.slice(5) - 2, 1))

function closedMonths(count = 24) {
  const out = []
  const t = new Date()
  for (let k = 1; k <= count; k++) out.push(ymKey(new Date(t.getFullYear(), t.getMonth() - k, 1)))
  return out
}
function periodOptions(frequency) {
  const months = closedMonths()
  if (frequency === 'month') return months.slice(0, 18).map(m => [m, ymLabel(m)])
  const done = new Set(months)
  const keys = []
  for (const m of months) {
    const y = m.slice(0, 4)
    const q = Math.ceil(+m.slice(5) / 3)
    const k = frequency === 'quarter' ? `${y}-Q${q}` : y
    const inPeriod = frequency === 'quarter'
      ? [1, 2, 3].map(i => `${y}-${String((q - 1) * 3 + i).padStart(2, '0')}`)
      : Array.from({ length: 12 }, (_, i) => `${y}-${String(i + 1).padStart(2, '0')}`)
    if (!keys.includes(k) && inPeriod.every(x => done.has(x))) keys.push(k)
  }
  return keys.map(k => [k, frequency === 'quarter' ? k.replace('-', ' ') : `Calendar ${k}`])
}

function contractValueAtMinimums(contract) {
  const months = contract.initial_term_months || 0
  const lines = contract.lines || []
  const perMonth = lines.filter(l => l.charge_kind !== 'one_time')
    .reduce((s, l) => s + Number(l.min_quantity || 0) * Number(l.rate || 0), 0)
  const oneTime = lines.filter(l => l.charge_kind === 'one_time').reduce((s, l) => s + Number(l.rate || 0), 0)
  let total = 0
  for (let m = 0; m < months; m++) {
    const step = contract.uplift_basis === 'annual' ? Math.floor(m / 12) : 0
    total += perMonth * Math.pow(1 + Number(contract.uplift_pct || 0) / 100, step)
  }
  return { total: total + oneTime, months, oneTime, annual: perMonth * 12 }
}

function billingOf(contract) {
  const sch = (contract.schedules || []).find(s => s.component === 'full' || s.component === 'minimum')
  if (!sch) return '—'
  const f = { monthly: 'Monthly', quarterly: 'Quarterly', annual: 'Annually' }[sch.frequency] || sch.frequency
  const t = { in_arrears: 'in arrears', in_advance: 'in advance', on_signature: 'on signature' }[sch.timing] || sch.timing
  return `${f} ${t}`
}

// `portal`: read through the customer-scoped /api/portal endpoints (the customer portal's
// Contract tab) instead of the admin ones. A non-admin there gets a customer payload
// (routers/portal.py: no SQL, no notes, no join-guard detail) and the customer wording
// below: no SQL promises, no Tech Landscape system id, no partition / join-guard check.
// Admins (on the customer page's Contract tab, or previewing the portal) see everything.
// Both tabs pass `portal`; the admin-endpoint mode (portal=false) has no caller since the
// standalone /admin/contracts page was removed.
export default function ContractView({ customerId, customerName: nameProp, embedded = false, portal = false }) {
  const { isAdmin } = useAuth()
  const internal = !portal || isAdmin
  const listContracts = portal ? getPortalContracts : getAdminContracts
  const evaluate = useCallback((id, freq, per) => (portal
    ? evaluatePortalContract(customerId, id, freq, per)
    : evaluateAdminContract(id, freq, per)), [portal, customerId])
  const [contracts, setContracts] = useState([])
  const [contractId, setContractId] = useState('')
  const [month, setMonth] = useState(closedMonths(1)[0])
  const [view, setView] = useState(null)            // evaluation of the selected month
  const [fallbackNote, setFallbackNote] = useState(null)
  const [open, setOpen] = useState({})
  const [frequency, setFrequency] = useState('month')
  const [period, setPeriod] = useState('')
  const [estimate, setEstimate] = useState(null)
  const [busy, setBusy] = useState(false)
  const [estBusy, setEstBusy] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (!customerId) return
    setContracts([]); setContractId(''); setView(null); setEstimate(null); setError(null)
    listContracts(customerId).then(res => {
      const list = res.contracts || []
      setContracts(list)
      if (list.length) setContractId(list[0].id)
    }).catch(e => setError(e.message))
  }, [customerId]) // eslint-disable-line react-hooks/exhaustive-deps

  const contract = contracts.find(c => c.id === contractId)
  // A contract billed quarterly / annually estimates by that period by default.
  useEffect(() => {
    if (!contract) return
    const sch = (contract.schedules || []).find(s => s.component === 'full' || s.component === 'minimum')
    setFrequency(sch?.frequency === 'quarterly' ? 'quarter' : sch?.frequency === 'annual' ? 'year' : 'month')
  }, [contractId]) // eslint-disable-line react-hooks/exhaustive-deps

  // Evaluate the selected month. If that month isn't final yet, fall back once to an
  // earlier month and say so -- an unsynced month is not a month of zero. A month is
  // final once data synced after it closed exists; until then its latest activity can
  // sit inside the month itself, so the fallback is then the month before.
  const loadMonth = useCallback(async (m, allowFallback) => {
    if (!contractId) return
    setBusy(true); setError(null)
    try {
      const r = await evaluate(contractId, 'month', m)
      if (!r.data_complete && allowFallback) {
        const latest = Object.values(r.latest_activity || {}).sort().pop()
        let lm = latest ? latest.slice(0, 7) : null
        if (lm && lm >= m) lm = prevMonth(m)
        if (lm) {
          const asOf = asOfOf(r)
          setFallbackNote(`${ymLabel(m)} isn't final yet (${asOf ? `data as of ${asOf}` : `latest activity ${latest}`}), so this shows ${ymLabel(lm)}.`)
          setMonth(lm)
          setView(await evaluate(contractId, 'month', lm))
          return
        }
      }
      setView(r)
    } catch (e) {
      setError(e.message)
    } finally {
      setBusy(false)
    }
  }, [contractId, evaluate])

  useEffect(() => { if (contractId) { setFallbackNote(null); loadMonth(closedMonths(1)[0], true) } }, [contractId, loadMonth])

  const options = useMemo(() => periodOptions(frequency), [frequency])
  useEffect(() => { setPeriod(options.length ? options[0][0] : ''); setEstimate(null) }, [options])

  async function runEstimate() {
    if (!contractId || !period) return
    setEstBusy(true); setError(null); setEstimate(null)
    try { setEstimate(await evaluate(contractId, frequency, period)) } catch (e) { setError(e.message) } finally { setEstBusy(false) }
  }

  const monthLines = useMemo(() => {
    const m = view?.months?.[0]
    return m ? Object.fromEntries(m.lines.map(l => [l.line_id, l])) : {}
  }, [view])

  const estRows = useMemo(() => {
    if (!estimate) return []
    const acc = {}
    for (const m of estimate.months) for (const l of m.lines) {
      const a = acc[l.line_id] || (acc[l.line_id] = { ...l, quantity: 0, billed_quantity: 0, months: 0, floored: 0 })
      a.quantity += l.quantity || 0; a.billed_quantity += l.billed_quantity || 0; a.months += 1
      if (l.floored) a.floored += 1
    }
    return Object.values(acc).map(a => ({ ...a, amount: estimate.lines_total[a.line_id]?.amount }))
  }, [estimate])

  const tcv = contract ? contractValueAtMinimums(contract) : null
  const order = view?.provenance?.order || []
  // Rule lines outside the first-match order: counted on their own rule, so their
  // people can also sit on a first-match line (e.g. a line the vendor bills in addition).
  const independent = order.length
    ? (contract?.lines || []).filter(l => l.quantity_source === 'rule' && !order.includes(l.id)) : []
  const lineSql = view?.provenance?.line_sql || {}
  const m0 = view?.months?.[0]
  const monthTotal = m0 ? Number(m0.total) : null
  const hasPepmType = (contract?.lines || []).some(l => l.billing_model === 'pepm_by_type')
  const orderRef = (contract?.documents || []).map(d => d.order_ref).filter(Boolean).join(' · ') || '—'
  const estLabel = frequency === 'month' && period ? ymLabel(period) : (options.find(o => o[0] === period) || [])[1]
  const customerName = nameProp || customerId

  return (
    <div className={`ac-page${embedded ? ' ac-embedded' : ''}`}>
        <div className="ac-wrap">
          <div className="ac-pickers">
            <label><span>Contract</span>
              <select value={contractId} onChange={e => setContractId(e.target.value)} disabled={!contracts.length}>
                {!contracts.length && <option>No contract on file</option>}
                {contracts.map(c => <option key={c.id} value={c.id}>{c.vendor || 'Contract'} · from {c.term_start || '—'} · {c.status}</option>)}
              </select>
            </label>
          </div>

          {error && <div className="ac-banner bad">{error}</div>}
          {!contract && customerId && !error && (
            <div className="ac-banner">No contract on file for {customerName}. Contracts are loaded from the signed order form.</div>
          )}

          {contract && (
            <>
              <div className="ac-eyebrow">Contract</div>
              <div className="ac-card ac-hdr">
                <div className="ac-hdr-sys">
                  <div className="ac-ico">{(contract.vendor || 'SYS').slice(0, 3).toUpperCase()}</div>
                  <div><div className="t">{contract.vendor || 'System'} · {customerName}</div><div className="s">{internal ? `Tech Landscape system ${contract.system_id} · ${contract.status}` : `Status: ${contract.status}`}</div></div>
                  {hasPepmType && <span className="ac-chip">PEPM by employee type</span>}
                </div>
                <div className="ac-grid">
                  <div><label className="ac-fl">Order ref</label><div className="ac-inp">{orderRef}</div></div>
                  <div><label className="ac-fl">Billing</label><div className="ac-inp">{billingOf(contract)}</div></div>
                  <div><label className="ac-fl">Period</label>
                    <select className="ac-inp" value={month} onChange={e => { setFallbackNote(null); setMonth(e.target.value); loadMonth(e.target.value, false) }}>
                      {closedMonths(18).map(m => <option key={m} value={m}>{ymLabel(m)}</option>)}
                    </select></div>
                  <div><label className="ac-fl">Uplift %</label><div className="ac-inp num">{contract.uplift_pct != null ? Number(contract.uplift_pct).toFixed(1) : '—'}</div></div>
                  <div><label className="ac-fl">Uplift basis</label><div className="ac-inp">{(contract.uplift_basis || '—').replace(/_/g, ' ')}</div></div>
                  <div><label className="ac-fl">Initial term</label><div className="ac-inp num">{contract.initial_term_months ? `${contract.initial_term_months} mo` : (contract.co_term ? 'co-term' : '—')}</div></div>
                  <div><label className="ac-fl">Minimum cadence</label><div className="ac-inp">{contract.minimum_cadence}</div></div>
                </div>
                {(contract.provided_products || []).length > 0 && (
                  <div className="ac-provided">
                    <div className="ac-fl">{internal ? 'Also on this contract (no direct contract with their maker)' : 'Also provided under this contract'}</div>
                    {contract.provided_products.map((p, i) => (
                      <div key={i} className="ac-provided-row">
                        <b>{p.product_name}</b>
                        {p.functions?.length > 0 && <span className="ac-muted"> · {p.functions.map(f => f.replace(/_/g, ' ')).join(', ')}</span>}
                        <span className="ac-chip">{providedThroughText(p, [], { customer: !internal })}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {fallbackNote && <div className="ac-banner">{fallbackNote}</div>}
              {view && !view.data_complete && (
                <div className="ac-banner bad"><b>Not a bill — {ymLabel(month)} isn't final.</b> {asOfOf(view) ? `The data is as of ${asOfOf(view)}; ` : 'The data carries no sync date; '}
                  a month is final once data synced after it closed is loaded. Until then a count can be short, and a 0 means "not loaded", not "nobody paid".</div>
              )}

              {view?.minimum_cadence === 'billing_period' && view?.billing_period_months > 1 && (
                <div className="ac-banner">Minimums are floored on each {view.billing_period_months === 3 ? 'quarter' : `${view.billing_period_months}-month period`}'s total,
                  so the month costs below are usage only. Use <b>Bill estimate → {view.billing_period_months === 3 ? 'Quarter' : 'Year'}</b> for what will be billed.</div>
              )}
              <div className="ac-sec-head"><div><h2>Subscription lines</h2>
                <p>Each line's count is its rule, run against synced data for {ymLabel(month)}{asOfOf(view) ? ` (data as of ${asOfOf(view)})` : ''}. {internal ? 'Expand a line to see the rule and the SQL it compiles to.' : 'Expand a line to see the rule that was checked.'}</p></div>
                {busy && <span className="ac-busy">Counting…</span>}</div>
              <div className="ac-colhead"><span></span><span>Bundle / employee type</span><span>Billing model</span><span>PEPM</span><span>Min</span><span>Count (rule)</span><span>Month cost</span><span></span></div>

              {(contract.lines || []).map((l, i) => {
                const r = monthLines[l.id]
                const pos = order.indexOf(l.id)
                const isOpen = !!open[l.id]
                const conds = l.quantity_definition?.conditions || []
                const sql = lineSql[l.id]
                return (
                  <div className="ac-line" key={l.id}>
                    <div className="ac-line-row">
                      <div className="ac-line-no">{l.line_no ?? i + 1}</div>
                      <div className="ac-ll"><div className="lbl">{l.label}</div>
                        <div className="sub">{(l.type_key || l.quantity_source).replace(/_/g, ' ')} · {l.product_type === 'service' ? 'Service' : 'Product'}{l.charge_kind === 'one_time' ? ' · one-time' : ''}</div></div>
                      <div className="ac-model">{MODEL_LABEL[l.billing_model] || l.billing_model}</div>
                      <div className="ac-mini">{Number(l.rate).toLocaleString('en-US', { minimumFractionDigits: 2 })}</div>
                      <div className="ac-mini">{l.min_quantity != null ? num(Number(l.min_quantity)) : '—'}</div>
                      <div className="ac-qty"><div className="v">{r?.quantity != null ? num(r.quantity) : '—'}</div>
                        <div className="src">{l.quantity_source === 'rule' ? '● rule' : l.quantity_source}</div></div>
                      <div className="ac-amt">{r ? money(r.amount) : (l.charge_kind === 'one_time' ? <span className="ac-muted">not this month</span> : '—')}
                        {r?.floored && <span className="ac-floor">floor</span>}</div>
                      {l.quantity_source === 'rule'
                        ? <button className={`ac-chev${isOpen ? ' open' : ''}`} onClick={() => setOpen(o => ({ ...o, [l.id]: !o[l.id] }))}>{isOpen ? '▾' : '▸'}</button>
                        : <span />}
                    </div>
                    {isOpen && (
                      <div className="ac-rule">
                        <div className="ac-rh">Count <b>distinct employees</b> per <b>calendar month</b> where <b>all</b> of:
                          <span className="ac-pos">{pos >= 0 ? `first match · position ${pos + 1} of ${order.length}` : 'counted independently'}</span></div>
                        {conds.map((c, ci) => {
                          const d = describe(c)
                          return (
                            <div className="ac-cond" key={ci}>
                              <div className="ac-parts"><span className="tag src">{d.src}</span><span className="tag">{d.field}</span>
                                <span className="tag">{d.op}</span>{d.val !== '' && <span className="tag">{d.val}</span>}
                                {isUnverified(c, sql?.unverified_joins) && <span className="tag unv">join unverified</span>}</div>
                            </div>
                          )
                        })}
                        <div className="ac-rule-foot"><span className="cnt">{r?.quantity != null ? num(r.quantity) : '—'}</span>
                          <span className="cl">people on this line · billed {r?.billed_quantity != null ? num(r.billed_quantity) : '—'} (min {num(Number(l.min_quantity || 0))})
                            {pos > 0 ? ' · after the lines above it claim their people' : ''}</span></div>
                        {internal && sql && <pre className="ac-sql">{sql.sql}{'\n\n-- params: '}{JSON.stringify(sql.params)}</pre>}
                      </div>
                    )}
                  </div>
                )
              })}

              {m0 && internal && (
                <div className="ac-card ac-part">
                  <h3>Partition check</h3>
                  <p>The {order.length} first-match lines put each person on exactly one of them, so none of those lines bills a person twice. Everyone else is the not-billed remainder.
                    {independent.length > 0 && ` ${independent.length === 1 ? 'One line is' : `${independent.length} lines are`} counted independently (${independent.map(l => l.label).join('; ')}): its rule is counted on its own, so its people can also be on a first-match line.`}</p>
                  <div className="ac-chips">
                    <span className="ac-pchip">No overlaps among first-match lines</span>
                    {independent.length > 0 && <span className="ac-pchip neutral">Counted independently: <b>{independent.length}</b></span>}
                    <span className="ac-pchip neutral">Not billed: <b>{num(m0.not_billed)}</b></span>
                    {Object.entries(m0.coverage || {}).map(([s, n]) => (
                      <span key={s} className={`ac-pchip${n > 0 ? ' neutral' : ' bad'}`}>{s.replace(/_/g, ' ')}: <b>{num(n)}</b> rows in {ymShort(month)}</span>
                    ))}
                    <span className={`ac-pchip${m0.guards_ok ? '' : ' bad'}`}>Join guards: <b>{m0.guards_ok ? 'ok' : 'FAILED'}</b></span>
                  </div>
                </div>
              )}

              {m0 && !internal && (
                <div className="ac-card ac-part">
                  <h3>Who is counted</h3>
                  <p>{order.length ? 'Each person counts on only one of the first-match lines: the first whose rule they match. ' : ''}
                    {independent.length > 0 ? `${independent.map(l => l.label).join('; ')} ${independent.length === 1 ? 'is' : 'are'} counted on ${independent.length === 1 ? 'its' : 'their'} own rule, so ${independent.length === 1 ? 'its' : 'their'} people can also count on a first-match line. ` : ''}
                    People who match no line are not billed.</p>
                  <div className="ac-chips">
                    <span className="ac-pchip neutral">Not billed: <b>{num(m0.not_billed)}</b></span>
                  </div>
                </div>
              )}

              <div className="ac-total">
                <div>
                  <div className="l">CONTRACT COST · {ymShort(month)}{view?.minimum_cadence === 'billing_period' && view?.billing_period_months > 1 ? ' · USAGE (FLOOR PER QUARTER)' : ''}</div>
                  <div className="s">{view?.data_complete === false ? `Incomplete — the month isn't final${asOfOf(view) ? ` (data as of ${asOfOf(view)})` : ''}`
                    : monthTotal != null ? `${billingOf(contract)} · ${moneyK(monthTotal * 12)}/yr at this month's run rate` : ''}</div>
                </div>
                <div className="vals">
                  <div className="v">{monthTotal == null ? '—' : view?.data_complete === false ? 'incomplete' : money(monthTotal)}</div>
                  {tcv.months > 0 ? (
                    <div className="tcv"><div className="l">TOTAL CONTRACT VALUE · AT MINIMUMS</div>
                      <div className="v">{moneyK(tcv.total)}</div>
                      <div className="s">{tcv.months} months{tcv.oneTime ? ` incl. ${money(tcv.oneTime)} one-time` : ''} · {contract.uplift_basis === 'annual' ? 'uplift each year' : 'no uplift inside the initial term'} · usage above minimums not included</div></div>
                  ) : (
                    <div className="tcv"><div className="l">ANNUAL VALUE · AT MINIMUMS</div>
                      <div className="v">{moneyK(tcv.annual)}</div>
                      <div className="s">{contract.co_term ? 'Co-termed' : 'No initial term stated'} — total contract value needs the term length · at signed rates · usage above minimums not included</div></div>
                  )}
                </div>
              </div>

              <div className="ac-card ac-estim">
                <h3>Bill estimate</h3>
                <p>Pick a closed period to see what the vendor should bill for it, before the invoice arrives.</p>
                <div className="ac-ctl">
                  <div><label className="ac-fl">Frequency</label>
                    <select className="ac-inp" value={frequency} onChange={e => setFrequency(e.target.value)}>
                      <option value="month">Month</option><option value="quarter">Quarter</option><option value="year">Year</option>
                    </select></div>
                  <div><label className="ac-fl">Period</label>
                    <select className="ac-inp" value={period} onChange={e => { setPeriod(e.target.value); setEstimate(null) }}>
                      {options.map(([k, lbl]) => <option key={k} value={k}>{lbl}</option>)}
                    </select></div>
                  <button className="nav-btn primary" onClick={runEstimate} disabled={estBusy || !period}>{estBusy ? 'Counting…' : 'Estimate bill'}</button>
                </div>
                {estimate && (
                  <>
                    {!estimate.data_complete && (
                      <div className="ac-banner bad"><b>Not a bill — part of {estLabel} isn't final</b>
                        {' '}({estimate.months.filter(m => !m.data_complete).map(m => ymLabel(m.period)).join(', ')}; data as of {asOfOf(estimate) || 'unknown'}, latest activity {Object.values(estimate.latest_activity || {}).sort().pop() || 'none'}).</div>
                    )}
                    {estimate.floor_note && <div className="ac-banner">{estimate.floor_note}</div>}
                    <div className="ac-est-head"><span>Expected bill for <b>{estLabel}</b></span>
                      <span className={`ac-est-v${estimate.data_complete ? '' : ' void'}`}>{estimate.data_complete ? money2(estimate.total) : 'incomplete'}</span></div>
                    <table className="ac-est">
                      <thead><tr><th>Line</th><th>Count{estimate.months.length > 1 ? ' (avg/mo)' : ''}</th><th>Billed qty</th><th>Rate</th><th>Amount</th></tr></thead>
                      <tbody>
                        {estRows.map(l => (
                          <tr key={l.line_id}><td>{l.label}</td>
                            <td>{l.quantity == null ? '—' : num(Math.round(l.quantity / l.months))}</td>
                            <td>{l.billed_quantity == null ? '—' : num(Math.round(l.billed_quantity / l.months))}{l.floored > 0 && <span className="ac-floor">floor{l.months > 1 ? ` ×${l.floored}` : ''}</span>}</td>
                            <td>{money2(l.rate)}</td><td>{money2(l.amount)}</td></tr>
                        ))}
                      </tbody>
                    </table>
                    <div className="ac-note">{estimate.minimum_cadence === 'billing_period'
                      ? `Minimums are floored on each billing period's total (${estimate.billing_period_months} months × minimum), counted from the term start.`
                      : 'Each month is floored separately (max(count, minimum) × rate), then summed across the period.'}</div>
                  </>
                )}
              </div>
              <div className="ac-note"><b>One screen:</b> contract terms and counting rules live together. {internal
                ? 'Counts come from the compiled rules run read-only against synced data; the SQL under each line is exactly what ran.'
                : "Counts come from each line's rule, run read-only against your synced data; expand a line to see the rule that was checked."}</div>
            </>
          )}
        </div>
    </div>
  )
}
